import { createContext, useContext, useMemo } from 'react'

import { useAdditionalFilters } from 'modules/Monitoring/GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import {
  FCWithChildren,
  RangeDirection,
  TimeRange,
  TimeScale,
  VolumeAggregationDimensionTarget,
  VolumeComparisonType,
} from 'modules/Monitoring/types'

import { AggregationDimensionValue } from '../TicketsVolumeDetails'

import { useRefetchOnWindowFocus } from './hooks/useRefetchOnWindowFocus'
import { useTicketsVolumeEffects } from './hooks/useTicketsVolumeEffects'
import { useTicketsVolumeHandlers } from './hooks/useTicketsVolumeHandlers'
import { useTicketsVolumeState } from './hooks/useTicketsVolumeState'
import { getDimensionTarget } from './utils/getDimensionTarget'

export type TicketsVolumeDetailsControls = {
  timeScale: TimeScale
  comparisonType: VolumeComparisonType
  aggregationDimension: AggregationDimensionValue
  timeRangePages: TimeRange[]
  currentTimeRangeIndex: number
}

export type TicketsVolumeDetailsContext = {
  timeScale: TimeScale
  comparisonType: VolumeComparisonType
  aggregationDimension: AggregationDimensionValue
  dimensionTarget: VolumeAggregationDimensionTarget | undefined
  currentTimeRange: TimeRange
  setCurrentTimeRange: (direction: RangeDirection) => void
  setTimeScale: (timeScale: TimeScale) => void
  setComparisonType: (comparisonType: VolumeComparisonType) => void
  setAggregationDimension: (aggregationDimension: AggregationDimensionValue) => void
  hasNextRange: boolean
}

export const TicketsVolumeDetailsContext = createContext<TicketsVolumeDetailsContext>(
  {} as TicketsVolumeDetailsContext,
)

export const useTicketsVolumeDetails = () => {
  const context = useContext(TicketsVolumeDetailsContext)
  if (!context) {
    throw new Error(
      'TicketsVolumeDetailsContext cannot be used outside TicketsVolumeDetailsContextProvider',
    )
  }
  return context
}

export const TicketsVolumeDetailsProvider: FCWithChildren = ({ children }) => {
  const { channel, regionedChannel } = useChannel()
  const { preparedFiltersForRequest, clearAdditionalFilters } = useAdditionalFilters()

  const { state, updateState } = useTicketsVolumeState(channel, regionedChannel)

  const {
    currentTimeRange,
    hasNextRange,
    handleCalculateTimeRangeParams,
    handleCalculateRangeUpdates,
    handleTimeScaleChange,
    handleComparisonTypeChange,
    handleAggregationDimensionChange,
    handleCurrentTimeRangeChange,
  } = useTicketsVolumeHandlers({
    state,
    updateState,
    preparedFiltersForRequest,
    channel,
    clearAdditionalFilters,
  })

  useTicketsVolumeEffects({
    updateState,
    calculateRangeUpdates: handleCalculateRangeUpdates,
    preparedFiltersForRequest,
  })

  useRefetchOnWindowFocus({
    timeScale: state.timeScale,
    comparisonType: state.comparisonType,
    timeRangePages: state.timeRangePages,
    currentTimeRangeIndex: state.currentTimeRangeIndex,
    calculateTimeRangeParams: handleCalculateTimeRangeParams,
  })

  const value = useMemo(
    () => ({
      timeScale: state.timeScale,
      comparisonType: state.comparisonType,
      aggregationDimension: state.aggregationDimension,
      dimensionTarget: getDimensionTarget(state.aggregationDimension),
      currentTimeRange,
      setCurrentTimeRange: handleCurrentTimeRangeChange,

      setTimeScale: handleTimeScaleChange,
      setComparisonType: handleComparisonTypeChange,
      setAggregationDimension: handleAggregationDimensionChange,
      hasNextRange,
    }),
    [
      state,
      currentTimeRange,
      hasNextRange,
      handleCurrentTimeRangeChange,
      handleTimeScaleChange,
      handleComparisonTypeChange,
      handleAggregationDimensionChange,
    ],
  )

  return (
    <TicketsVolumeDetailsContext.Provider value={value}>
      {children}
    </TicketsVolumeDetailsContext.Provider>
  )
}
